import React, { useState, useEffect } from "react";
import { editSize } from "../../../services/api";
import { showErrorToast, showSuccessToast } from "../../../services/toast"; // 1. Import showSuccessToast

const ROOM_CATEGORIES = [
  "Living Room",
  "Kitchen",
  "Bathroom",
  "Outdoor",
  "Bedroom",
  "Commercial",
  "Elevation",
  "Counter Tops",
  "Step-Riser",
  "Waterline",
];

export function EditSizeModal({ isOpen, sizeData, onClose, onSuccess }) {
  const [editLoading, setEditLoading] = useState(false);
  const [editFormData, setEditFormData] = useState({
    name: "",
    width: "",
    height: "",
    unit: "mm",
    default_layout: "grid",
    room_categories: [],
  });

  useEffect(() => {
    if (sizeData) {
      setEditFormData({
        name: sizeData.name || "",
        width: sizeData.width || "",
        height: sizeData.height || "",
        unit: sizeData.unit || "mm",
        default_layout: sizeData.default_layout || "grid",
        room_categories: Array.isArray(sizeData.room_categories)
          ? sizeData.room_categories
          : typeof sizeData.room_categories === "string"
          ? sizeData.room_categories.split(",").map((c) => c.trim())
          : [],
      });
    }
  }, [sizeData]);

  if (!isOpen || !sizeData) return null;

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleEditRoomCategoryChange = (category) => {
    setEditFormData((prev) => ({
      ...prev,
      room_categories: prev.room_categories.includes(category)
        ? prev.room_categories.filter((c) => c !== category)
        : [...prev.room_categories, category],
    }));
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setEditLoading(true);

    try {
      const formDataToSend = new FormData();
      Object.keys(editFormData).forEach((key) => {
        if (editFormData[key] !== "" && editFormData[key] !== null) {
          if (key === "room_categories") {
            editFormData.room_categories.forEach((cat) => {
              formDataToSend.append("room_categories", cat);
            });
          } else {
            formDataToSend.append(key, editFormData[key]);
          }
        }
      });

      const response = await editSize(sizeData.id, formDataToSend);
      
      // 2. Add the success toast trigger here
      if (response.success || response.status === 200) {
        showSuccessToast(response.message || "Product size updated successfully!");
        onSuccess();
        onClose();
      }
    } catch (err) {
      showErrorToast(err.message || "Failed to update product size");
    } finally {
      setEditLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 overflow-y-auto">
      <div className="bg-white rounded-xl p-6 max-w-3xl w-full mx-4 my-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-lg font-semibold text-gray-900">
            Edit Product Size
          </h3>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700"
            type="button"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          </button>
        </div>

        <form
          onSubmit={handleEditSubmit}
          className="space-y-4 max-h-[70vh] overflow-y-auto pr-2"
        >
          <div className="grid grid-cols-2 gap-4">
            <div className="col-span-2">
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Size Name
              </label>
              <input
                type="text"
                name="name"
                value={editFormData.name}
                onChange={handleEditChange}
                placeholder="e.g., 600 x 1200 mm"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Width
              </label>
              <input
                type="number"
                step="0.01"
                name="width"
                value={editFormData.width}
                onChange={handleEditChange}
                placeholder="Product width"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Height
              </label>
              <input
                type="number"
                step="0.01"
                name="height"
                value={editFormData.height}
                onChange={handleEditChange}
                placeholder="Product height"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Unit
              </label>
              <select
                name="unit"
                value={editFormData.unit}
                onChange={handleEditChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              >
                <option value="mm">Millimeters (mm)</option>
                <option value="cm">Centimeters (cm)</option>
                <option value="inch">Inches</option>
                <option value="feet">Feet</option>
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Default Layout
              </label>
              <select
                name="default_layout"
                value={editFormData.default_layout}
                onChange={handleEditChange}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-accent focus:border-transparent outline-none"
              >
                <option value="grid">Grid</option>
                <option value="brick">Brick</option>
                <option value="vertical">Vertical</option>
                <option value="horizontal">Horizontal</option>
                <option value="herringbone">Herringbone</option>
              </select>
            </div>

            <div className="col-span-2">
              <label className="block text-sm font-medium mb-3 text-gray-700">
                Show/Hide Size By Room Category
              </label>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-2">
                {ROOM_CATEGORIES.map((category) => (
                  <label
                    key={category}
                    className="flex items-center space-x-2 cursor-pointer"
                  >
                    <input
                      type="checkbox"
                      checked={editFormData.room_categories.includes(category)}
                      onChange={() => handleEditRoomCategoryChange(category)}
                      className="w-4 h-4 text-accent border-gray-300 rounded focus:ring-accent"
                    />
                    <span className="text-sm text-gray-600">{category}</span>
                  </label>
                ))}
              </div>
            </div>
          </div>

          <div className="flex gap-3 justify-end pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={editLoading}
              className="px-4 py-2 bg-accent text-white rounded-lg hover:bg-accent2 transition-colors disabled:cursor-not-allowed disabled:bg-gray-300"
            >
              {editLoading ? "Saving..." : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}